import React, { useState, useRef, useMemo } from "react";
import { Button, Empty, Input, InputRef, Space, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { IComponentField } from '../../../core/types/IComponentField';
import { ComponentsHelper } from "../../../core/services/ComponentHelper";
import IconTextButton from '../../../repertoire/components/iconTextButton/IconTextButton';
import ShowMore from '../../../repertoire/components/showMore/ShowMore';
import { EXTERNAL_SVG, TRIGGER_AUTO_ICON, DELETE_ICON } from '../../../repertoire/IconConsts';
import { ColumnType, FilterConfirmProps } from "antd/lib/table/interface";
import { CONFIRM_UNDO_PROMPT_STATE_KEY } from '../../Consts';
import { IStoragePathItemData } from "../../types/IStoragePathItemData";
import { IComponentDataItem } from "../../../core/types/IComponentDataItem";
import { IDistribution } from "../../../repertoire/types/usageTypes/IDistribution";
import { ILookupInstance } from "../../../lookup/types/ILookup";
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";
import { ITreeData } from "../../../repertoire/types/ITreeData";
import { IFileSystem } from "../../types/IFileSystem";
import { IUsagePool } from "../../../repertoire/types/usageTypes/IUsagePool";
import { IJobState } from '../../../redux/types/IJobState';
import Highlighter from 'react-highlight-words';
import { editingChanged, fileMetadataChanged } from '../../../redux/reducers/DataIngestionReducer';
import { IYesNoPromptViewModalProps } from "../../../repertoire/components/modalViews/yesNoPromptView/YesNoPromptView";
import { hideModal, showYesNoPrompt } from '../../../redux/reducers/ModalReducer';
import DropdownDataInput from '../../../repertoire/components/dropdownDataInput/DropdownDataInput';
import DropdownTreeDataInput from "../../../repertoire/components/dropdownTreeDataInput/DropdownTreeDataInput";
import { ATTACHMENT_TYPE_TABLE_HEADER_FIELD, REMOVE, NO_DATA_MESSAGE_FIELD } from "../containerDetailsWindow/ContainerDetailsWindowFields";
import { ITabReduxItem } from "../../../redux/types/ITabReduxItem";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import {
    USAGE_INGESTION_MODAL_HEADER,
    SEARCH_VIEW_LICENSES_FILEIMPORT,
    CONTRIBUTORS_KEY,
    USAGE_WORK_LOWER,
    USAGE_PRODUCT_LOWER,
    REPERTOIRE_WORK_LOWER,
    REPERTOIRE_CUE_LOWER,
    REPERTOIRE_PRODUCT_LOWER,
    REPERTOIRE_LOWER,
    USAGE_TO_WORK_MATCH_TYPE_CODE,
    USAGE_TO_PRODUCT_MATCH_TYPE_CODE,
    USAGE_WORK_BASED_PATH,
    USAGE_PRODUCT_BASED_PATH
} from "../../../repertoire/Consts";
import { CANCEL_SORTING, NO_DATA, SORT_ASCENDING, SORT_DESCENDING } from "../../../membersportal/Consts";
import { IDataIngestionTableRow } from "../../types/IDataIngestionTableRow";

interface IDataIngestionTableProps {
    containerDetailsWindowComponentData: IComponentDataItem;
    storagePaths: IStoragePathItemData[];
    storagePathItemData: IComponentDataItem;
    distributions: IDistribution[];
    destinations: ILookupInstance[];
    matchingSources: ITreeData[];
    formats: ILookupInstance[];
    sources: ITreeData[];
    titleContains: string;
    distributionsFilter: string[];
    selectedFileSystem: IFileSystem;
    isRepertoireModule: boolean;
    pools: IUsagePool[];
    jobs: IJobState[];
    canEditMetadata: boolean;
    displayAttachmentType: boolean;
    displayRemoveColumn: boolean;
    attchmentTypeLookupOptions: ILookupInstance[];
    activeTab: number;
    tabs: ITabReduxItem[];
    determineIsReadOnly: (storagePathFullName: string) => boolean;
    deleteFile: (fullName: string, name: string) => void;
    editingChanged: typeof editingChanged;
    matchNow: (data: IStoragePathItemData) => void;
    undoUsageIngestion?: (fullFilePath: string) => void;
    showYesNoPrompt: typeof showYesNoPrompt;
    hideModal: typeof hideModal;
    fileMetadataChanged: typeof fileMetadataChanged;
    getDistributionDetails: (distributionID: number, lookupValues: ILookupDictionary) => void;
    getUsagePool: (poolId: number, matchingSources: ITreeData[], isPoolMaintainence?: boolean) => void;
    openDirectory: (filesystem: IFileSystem, sources: ITreeData[]) => void;
    showSearchModal: (modalProps: any, searchViewKey: string) => void;
    removeAttachmentFromMaintenanceScreen: (fullName: string, guid: string, index: number) => void;
    onDownloadFile: (data: IStoragePathItemData, isRepertoireModule: boolean) => void;
    viewData?: any;
    attachmentTypeChanged: (value: string, fieldName: string, fullName: string) => void;
    attachmentTypeColumnWidth?: number | string;
}
const DataIngestionTable: React.FC<IDataIngestionTableProps> = (props) => {

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [attachmentTypeCodes, setAttachmentTypeCodes] = useState<Record<string, string>>({});
    const dir = props.selectedFileSystem?.fullName ?? "";

    const sources = props.sources ?? [];
    const pools = props.pools ?? [];
    const distributions = props.distributions ?? [];

    const usageType = useMemo(() => {
        if (!dir) return null;

        const parts = dir.split("/");
        return parts[1] ?? null;
    }, [dir]);

    const sourceType = useMemo(() => {
        if (usageType === USAGE_WORK_BASED_PATH) return USAGE_TO_WORK_MATCH_TYPE_CODE;
        if (usageType === USAGE_PRODUCT_BASED_PATH) return USAGE_TO_PRODUCT_MATCH_TYPE_CODE;
        return null;
    }, [usageType]);

    const filteredSources = useMemo(() => {
        if (!sourceType) return sources;
        return sources.filter(source => source.matchType === sourceType);
    }, [sources, sourceType]);

    const sourceById = useMemo(() => {
        return new Map(
            filteredSources
                .filter(source => source.id !== undefined && source.id !== null)
                .map(source => [source.id!.toString(), source])
        );
    }, [filteredSources]);

    const sourceByHierarchy = useMemo(() => {
        return new Map(
            filteredSources
                .filter(source => !!source.hierarchy)
                .map(source => [source.hierarchy, source])
        );
    }, [filteredSources]);

    const poolsBySourceId = useMemo(() => {
        const map = new Map<string, IUsagePool[]>();

        pools.forEach(pool => {
            pool.sources?.forEach(poolSource => {
                const key = poolSource.source?.toString();
                if (!key) return;

                if (!map.has(key)) {
                    map.set(key, []);
                }

                map.get(key)!.push(pool);
            });
        });
        return map;
    }, [pools]);

    const poolByIdOrCode = useMemo(() => {
        const map = new Map<string, IUsagePool>();

        pools.forEach(pool => {
            if (pool.poolId !== undefined && pool.poolId !== null) {
                map.set(pool.poolId.toString(), pool);
            }

            if (pool.poolCode) {
                map.set(pool.poolCode, pool);
            }
        });

        return map;
    }, [pools]);

    const distributionsByPoolCode = useMemo(() => {
        const map = new Map<string, IDistribution[]>();

        distributions
            .filter(distribution => distribution.distributionStatus !== "Closed")
            .forEach(distribution => {
                distribution.distributionPools?.forEach(distributionPool => {
                    const key = distributionPool.poolCode;
                    if (!key) return;

                    if (!map.has(key)) {
                        map.set(key, []);
                    }

                    map.get(key)!.push(distribution);
                });
            });
        return map;
    }, [distributions]);

    function renderTextInputWithShowMore(record, field, showSearchModal, label, modalKey, onChange) {
        return (
            <>
                <TextDataInput
                    value={record?.[field.name] || ''}
                    changeData={(value) => onChange(value, field.name, record?.key, true)}
                    readOnly={false}
                    isHidden={false}
                    fieldName={field.name}
                />
                <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                    <ShowMore
                        options={[{
                            text: label,
                            onClick: () => showSearchModal(record, modalKey),
                            icon: EXTERNAL_SVG,
                        }]}
                    />
                </span>
            </>
        );
    }

    function formatFileSize(bytes: number, decimals = 2) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    function renderDropdownInput(options, value, fieldName, record, onChange, index) {
        return (
            <div className="dropDownInputIngestionTable">
                <DropdownDataInput
                    options={options}
                    changeData={(val, field) => onChange(val, field, record.key, true)}
                    value={value}
                    fieldName={fieldName}
                    allowNull={true}
                    isHidden={false}
                    index={index}
                    useComboBoxAsMenuWidth={false}
                />
            </div>
        );
    }

    const getActionIconsColumn = () => ({
        title: '',
        dataIndex: 'actionIcons',
        key: 'actionIcons',
        width: '2em',
        render: (_text: string, record: IDataIngestionTableRow) => {
            const dir: string = props.selectedFileSystem?.fullName ?? '';
            const parts = dir ? dir.split('/') : [];
            const showUndoIngestion =
                parts[0] === 'usage' &&
                (
                    (parts[3] === 'archive') ||
                    (parts[1] === 'society' && parts[2] === 'archive')
                );

            return (
                <div style={{ display: 'flex', gap: 4 }}>
                    {showUndoIngestion && (
                        <span title="Undo ingestion">
                            <IconTextButton
                                key="undo"
                                icon="icon iconHover node-icon ms-Icon ms-Icon--Undo"
                                onClick={() => onUndoIngestion(record)}
                            />
                        </span>
                    )}
                    {!record.isDirectory ? (
                        <span title="Download file">
                            <IconTextButton
                                key="download"
                                icon="icon iconHover node-icon ms-Icon ms-Icon--Download"
                                onClick={() => onDownloadFile(record)}
                            />
                        </span>
                    ) : (
                        <span title="Open folder">
                            <IconTextButton
                                key="download"
                                icon="icon node-icon ms-Icon ms-Icon--FabricFolder"
                            />
                        </span>
                    )}
                </div>
            );
        }
    });

    const getDynamicActionColumn = () => ({
        title: '',
        dataIndex: 'dynamicAction',
        key: 'dynamicAction',
        width: '1em',
        render: (_text: string, record: IDataIngestionTableRow) => {
            const canEditMetadata = props.canEditMetadata;
            const isSyncInput = record?.key?.startsWith("repertoire/works/sync/input");
            const isReadOnly = props.determineIsReadOnly(record?.key);
            const isEditing = record?.isEditing;
            if (record?.inProgress) {
                return (
                    <IconTextButton
                        icon="icon ms-Icon ms-Icon--SyncOccurence"
                    />
                );
            }
            else if (!isEditing && canEditMetadata && !isSyncInput) {
                return (
                    <ShowMore
                        data-testid="show-more"
                        options={[
                            {
                                text: "Edit",
                                onClick: () => { handleEditingChanged(record) },
                                icon: EXTERNAL_SVG,
                            },
                            {
                                text: "Process Now",
                                onClick: () => { matchNow(record) },
                                icon: TRIGGER_AUTO_ICON,
                                isIcon: true
                            },
                            {
                                text: "Delete",
                                onClick: () => { onDeleteFile(record) },
                                icon: DELETE_ICON,
                                isIcon: true
                            }
                        ]}
                        isReadOnly={isReadOnly}
                    />
                );
            }
            return {};
        }
    });

    const getColumnSearchProps = (dataIndex: string, name: string): ColumnType<IDataIngestionTableRow> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`${props.viewData['search']} ${name}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {props.viewData['search']}
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {props.viewData['reset']}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        {props.viewData['filter']}
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        {props.viewData['close']}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            return (record[dataIndex] ?? '')
                .toString().toLowerCase()
                .includes((value as string).toLowerCase())
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#fcfa5d', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: string,
    ) => {
        confirm();
        setSearchText(selectedKeys[0] ?? '');
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const onDownloadFile = (record: IDataIngestionTableRow) => {
        const { onDownloadFile, isRepertoireModule, storagePaths } = props;
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        if (storageItem) {
            onDownloadFile(storageItem, isRepertoireModule);
        }
    };

    const onRemoveFile = (record: IDataIngestionTableRow) => {
        const { isRepertoireModule, removeAttachmentFromMaintenanceScreen } = props;
        const index = data.findIndex((item) => item.key === record.key);
        if (!record.isDirectory && isRepertoireModule) {
            const splitName: string[] = (record.key ?? '').split('/');
            removeAttachmentFromMaintenanceScreen(record.key, splitName[splitName.length - 1], index);
        }
    }

    const handleEditingChanged = (record: IDataIngestionTableRow) => {
        const { editingChanged, storagePaths } = props;
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        if (storageItem) {
            editingChanged(storageItem);
        }
    }

    const onDeleteFile = (record: IDataIngestionTableRow) => {
        const { deleteFile } = props;
        deleteFile?.(record?.key ?? '', record?.FilenameTableHeader ?? '');
    }

    const matchNow = (record: IDataIngestionTableRow) => {
        const { matchNow, storagePaths } = props;
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        if (storageItem) {
            matchNow(storageItem);
        }
    }

    const onUndoIngestion = (record: IDataIngestionTableRow) => {
        const { undoUsageIngestion, storagePathItemData, showYesNoPrompt, hideModal, storagePaths } = props;
        
        const storageItem = storagePaths.find(item => item.fullName === record.key);
        const fullFilePath = `${storageItem.path}/${storageItem.name}`;

        if (!record.isDirectory && !record.inProgress && !record.isDirty && !record.isEditing) {
            if (record.key.split('/')[0] === 'usage') {
                const message: string = storagePathItemData.fields.find(f => f.name === CONFIRM_UNDO_PROMPT_STATE_KEY)?.data ?? '';
                const props: IYesNoPromptViewModalProps = {
                    yesCallback: () => {
                        undoUsageIngestion(fullFilePath);
                        hideModal();
                    },
                    noCallback: () => {
                        hideModal();
                    }
                }
                showYesNoPrompt(USAGE_INGESTION_MODAL_HEADER, props, undefined, message);
            }
        }
    }
    const handleAttachmentTypeChanged = (value: string, fieldName: string, fullName: string) => {
        setAttachmentTypeCodes(prev => ({ ...prev, [fullName]: value }));
        props.attachmentTypeChanged(value, fieldName, fullName);
    };
    function getFilteredData(row: IDataIngestionTableRow) {
        let sourceValue = row?.source;
        let poolCodeValue: any = row?.poolCode;

        if (sourceValue) {
            const foundSource = sourceById.get(sourceValue.toString());
            if (foundSource) {
                sourceValue = foundSource.hierarchy;
            }
        }

        if (poolCodeValue && typeof poolCodeValue === "object") {
            if (poolCodeValue.poolCode) {
                poolCodeValue = poolCodeValue.poolCode;
            } else if (poolCodeValue.id) {
                const foundPool = poolByIdOrCode.get(poolCodeValue.id.toString());
                poolCodeValue = foundPool?.poolCode ?? poolCodeValue;
            }
        } else if (poolCodeValue) {
            const foundPool = poolByIdOrCode.get(poolCodeValue.toString());
            poolCodeValue = foundPool?.poolCode ?? poolCodeValue;
        }

        const sourceObj = sourceValue
            ? sourceByHierarchy.get(sourceValue)
            : undefined;

        const filteredPools = sourceObj
            ? poolsBySourceId.get(sourceObj.id?.toString()) ?? []
            : [];

        const filteredDistributions = poolCodeValue
            ? distributionsByPoolCode.get(poolCodeValue.toString()) ?? []
            : [];

        const customNotebooks =
            sourceObj?.customNotebooks?.filter(notebook => notebook !== null && notebook !== "") ?? [];

        return {
            filteredPools,
            filteredDistributions,
            customNotebooks
        };
    }

    const columns = useMemo(() => {
        const {
            storagePathItemData,
            selectedFileSystem,
            distributions,
            pools,
            formats,
            sources,
            destinations,
            containerDetailsWindowComponentData,
            fileMetadataChanged,
            displayAttachmentType,
            displayRemoveColumn,
            attchmentTypeLookupOptions,
            showSearchModal,
            attachmentTypeColumnWidth
        } = props;

        const dir: string = selectedFileSystem.fullName;
        const fields: IComponentField[] = [
            ...storagePathItemData.fields.filter(f => f.componentInstance === dir),
            ...containerDetailsWindowComponentData.fields.filter(f =>
                ["FilenameTableHeader", "LastModifiedTableHeader", "TrackingInformationTableHeader"].includes(f.name)
            )
        ];

        let orderedFields = ComponentsHelper.orderFieldsBySpecificField(fields, "order");
        if (!orderedFields || orderedFields.length === 0) {
            orderedFields = fields;
        }

        const nameFieldIndex = orderedFields.findIndex(field => field.name === "FilenameTableHeader");
        if (nameFieldIndex > -1) {
            const field = orderedFields.splice(nameFieldIndex, 1)[0];
            orderedFields.unshift(field);
        }

        const fieldNamesToMove = ["LastModifiedTableHeader", "TrackingInformationTableHeader"];
        fieldNamesToMove.forEach(name => {
            const fieldIndex = orderedFields.findIndex(field => field.name === name);
            if (fieldIndex > -1) {
                const field = orderedFields.splice(fieldIndex, 1)[0];
                orderedFields.push(field);
            }
        });

        if (displayAttachmentType) {
            const attachmentField = containerDetailsWindowComponentData.fields.find(f => f.name === ATTACHMENT_TYPE_TABLE_HEADER_FIELD);
            if (attachmentField) orderedFields = [...orderedFields, attachmentField];
        }
		
        if (displayRemoveColumn) {
            const removeField = containerDetailsWindowComponentData.fields.find(f => f.name === REMOVE);
            if (removeField) orderedFields = [...orderedFields, removeField];
        }
        const dynamicStringSorter = (fieldName: string) => (a, b) => (a[fieldName] || '').localeCompare(b[fieldName] || '');

        const fieldsToRender = orderedFields.map(field => {
            switch (field.name) {
                case 'FilenameTableHeader':
                    return {
                        title: (
                            <div
                                style={{
                                    maxWidth: 150,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {field.data}
                            </div>
                        ),
                        dataIndex: field.name,
                        sorter: dynamicStringSorter(field.name),
                        key: field.name,
                        ...getColumnSearchProps(field.name as string, field.data as string),
                    };
                case 'source':

                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: filteredSources.map(s => ({ text: s.hierarchy, value: s.id })),
                        filterSearch: true,
                        onFilter: (value, record) => {
                            const sourceObj = sourceById.get(value.toString());
                            if (!sourceObj) return false;
                            return record?.source === sourceObj.hierarchy;
                        },
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            if (record.isEditing) {
                                let value = record.source || '';
                                if (value && filteredSources && filteredSources.length > 0) {
                                    const found = sourceById.get(value?.toString());
                                    if (found) {
                                        value = found.hierarchy;
                                    }
                                }
                                return (
                                    <DropdownTreeDataInput
                                        options={filteredSources}
                                        changeData={(selectedValue, fieldName) => {
                                            let hierarchy = selectedValue;
                                            if (selectedValue) {
                                                const selectedValueString = selectedValue.toString();

                                                const foundById = sourceById.get(selectedValueString);
                                                const foundByHierarchy = sourceByHierarchy.get(selectedValueString);

                                                if (foundById) {
                                                    hierarchy = foundById.hierarchy;
                                                } else if (foundByHierarchy) {
                                                    hierarchy = foundByHierarchy.hierarchy;
                                                }
                                            }

                                            fileMetadataChanged(hierarchy, fieldName, record.key, true);
                                        }}
                                        value={value}
                                        fieldName={field.name}
                                        allowNull={true}
                                        isHidden={false}
                                        showSearch={true}
                                    />
                                );
                            } else {
                                return text || '';
                            }
                        }
                    };
                case 'distributionCode':
                    return {
                        render: (text, record, index) => {
                            const { filteredDistributions } = getFilteredData(record);
                            let options = (filteredDistributions || []).map(dist => ({ code: dist.distributionCode, description: dist.distributionCode }));
                            if (record.distributionCode && !options.some(opt => opt.code === record.distributionCode)) {
                                options = [{ code: record.distributionCode, description: record.distributionCode }, ...options];
                            }
                            if (record.isEditing) {
                                return renderDropdownInput(options, record.distributionCode || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        },
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: distributions.map(d => ({ text: d.distributionCode, value: d.distributionCode })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.distributionCode?.includes(value?.toString()),
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'poolCode':
                    return {
                        render: (text, record, index) => {
                            const { filteredPools } = getFilteredData(record);
                            let options = (filteredPools || []).map(pool => ({ code: pool.poolCode, description: pool.poolCode }));
                            if (record.poolCode && !options.some(opt => opt.code === record.poolCode)) {
                                options = [{ code: record.poolCode, description: record.poolCode }, ...options];
                            }
                            if (record.isEditing) {
                                return renderDropdownInput(options, record.poolCode || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        },
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: pools.map(p => ({ text: p.poolCode, value: p.poolCode })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.poolCode && record?.poolCode === value.toString(),
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'format': {
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: formats.map(s => ({ text: s.description, value: s.description })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.format?.includes(value?.toString()),
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            if (record.isEditing) {
                                let customNotebooks = [];
                                if (filteredSources && record && record.source) {
                                    let sourceValue = record.source;
                                    const found = filteredSources.find(s => s.id === sourceValue || s.id?.toString() === sourceValue?.toString());
                                    if (found) {
                                        sourceValue = found.hierarchy;
                                    }
                                    const sourceObj = filteredSources.find(s => s.hierarchy === sourceValue);
                                    if (sourceObj && sourceObj.customNotebooks) {
                                        customNotebooks = sourceObj.customNotebooks.filter(notebook => notebook);
                                    }
                                }
                                let formatsFiltered = formats.filter(format =>
                                    format.extraFields == null || (
                                        format.extraFields["DataType"].split(',').some(f => dir.indexOf(f) >= 0) &&
                                        format.extraFields["InOut"] === "input"
                                    )
                                );
                                if (customNotebooks.length > 0) {
                                    customNotebooks.forEach(customNotebook => {
                                        formatsFiltered.push({
                                            typeID: 0,
                                            code: `${customNotebook}.ipynb`,
                                            description: `${customNotebook}.ipynb`
                                        });
                                    });
                                }
                                const options = formatsFiltered.map((f) => ({ code: f.description, description: f.description }));
                                return renderDropdownInput(options, record.format || '', field.name, record, fileMetadataChanged, index);
                            } else {
                                return text || '';
                            }
                        }
                    };
                }
                case "outputFormat": {
                    let formatsFiltered = formats.filter(format =>
                        format.extraFields == null ||
                        (format.extraFields["DataType"].split(',').some(f => dir.indexOf(f) >= 0) &&
                            format.extraFields["InOut"] === "output")
                    )
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: formatsFiltered.map(s => ({ text: s.description, value: s.description })),
                        filterSearch: true,
                        onFilter: (value, record) => record?.outputFormat?.includes(value?.toString()),
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            const options = formatsFiltered.map((f) => ({ code: f.description, description: f.description }));
                            if (record.isEditing) {
                                let val = record.outputFormat;
                                let rec = options.find(o => o.code === val);
                                if (!rec)
                                    val = ''

                                return renderDropdownInput(options, val, field.name, record, fileMetadataChanged, index);
                            } else {
                                let rec = options.find(o => o.code === text);
                                if (rec)
                                    return rec.description;

                                return ''
                            }
                        }
                    };
                }
                case 'destination':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        filters: destinations.map(s => ({ text: s.description, value: s.description })),
                        filterSearch: true,
                        onFilter: (value, record) => {
                            return record?.destination?.includes(value?.toString());
                        },
                        sorter: dynamicStringSorter(field.name),
                        render: (text, record, index) => {
                            //TODO: Refactor this section so its not hardcoded.
                            //It would be better to be configurable from the backend.
                            let options = destinations.map((d) => ({ code: d.description, description: d.description }));
                            const repertoireIndex = "Repertoire index";
                            const repertoireModule = "Repertoire module";
                            const repertoireModuleDirect = "Repertoire module direct";
                            const repertoireExternalWorkNumbers = "Repertoire External Work Numbers";
                            const repertoireContributorsUpdate = "Repertoire Contributors Update";
                            const workMatchHistory = "Work Match History";
                            const matchOnly = "Match only";

                            const productMatchHistory = "Product Match History";
                            const usageModule = "Usage module";
                            const usageModuleWithoutMatching = "Usage Module Without Matching";

                            if (record?.key?.slice(0, 16) == REPERTOIRE_WORK_LOWER)
                                options = options.filter(o => o.code == repertoireModule
                                    || o.code == repertoireIndex
                                    || o.code == repertoireModuleDirect
                                    || o.code == repertoireExternalWorkNumbers
                                    || o.code == repertoireContributorsUpdate
                                    || o.code == workMatchHistory);
                            else if (record?.key?.slice(0, 15) == REPERTOIRE_CUE_LOWER)
                                options = options.filter(o => o.code == repertoireModule
                                    || o.code == repertoireIndex
                                    || o.code == repertoireModuleDirect
                                    || o.code == repertoireExternalWorkNumbers
                                    || o.code == repertoireContributorsUpdate
                                    || o.code == productMatchHistory);
                            else if (record?.key?.slice(0, 18) == REPERTOIRE_PRODUCT_LOWER)
                                options = options.filter(o => o.code == repertoireModule
                                    || o.code == repertoireIndex
                                    || o.code == repertoireModuleDirect
                                    || o.code == repertoireExternalWorkNumbers
                                    || o.code == repertoireContributorsUpdate
                                    || o.code == productMatchHistory);
                            else if (record?.key?.slice(0, 10) == USAGE_WORK_LOWER || record?.key?.slice(0, 13) == USAGE_PRODUCT_LOWER)
                                options = options.filter(o => o.code == usageModule ||
                                    o.code == usageModuleWithoutMatching);
                            else if (record?.key?.slice(0, 10) == REPERTOIRE_LOWER)
                                options = options.filter(o => o.code == repertoireModule);

                            if (record.isEditing) {
                                let val = record.destination;
                                let rec = options.find(o => o.code === val);
                                if (!rec)
                                    val = ''
                                return renderDropdownInput(options, val, field.name, record, fileMetadataChanged, index);
                            } else {
                                let rec = options.find(o => o.code === text);
                                if (rec)
                                    return rec.description;

                                return ''
                            }
                        }
                    };
                case 'LastModifiedTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'TrackingInformationTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'batchID':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: dynamicStringSorter(field.name),
                    };
                case 'fileSize':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        sorter: (a, b) => {
                            const aSize = typeof a.fileSize === 'number' ? a.fileSize : 0;
                            const bSize = typeof b.fileSize === 'number' ? b.fileSize : 0;
                            return aSize - bSize;
                        },
                        render: (text, record) =>
                            typeof record.fileSize === 'number'
                                ? formatFileSize(record.fileSize)
                                : '',
                    };
                case 'AttachmentTypeTableHeader':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        ...(attachmentTypeColumnWidth
                            ? { width: attachmentTypeColumnWidth }
                            : {}),
                        sorter: dynamicStringSorter(field.name),
                        render: (_text, record, index) => {
                            const options = attchmentTypeLookupOptions.map((d) => ({ code: d.code, description: d.description }));
                            return (
                            <div className="attachmentTypeCell">
                                    {renderDropdownInput( 
                                    options,
                                    attachmentTypeCodes[record.key] ?? record?.attachmentTypeCode ?? '',
                                    field.name, 
                                    record,
                                    handleAttachmentTypeChanged,
                                    index,
                                )
                                }
                            </div>)
                            
                        }
                    };
                case 'remove_action':   
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        align: 'center' as const,
                        width: 100,
                        render: (_text, record) => {
                            return (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    margin: 0,
                                    padding: 0
                                }}>
                                    <IconTextButton
                                        onClick={() => onRemoveFile(record)}
                                        icon={DELETE_ICON}
                                        tooltip="Remove attachment"
                                    />
                                </div>
                            );

                        }
                    };
                case 'rightHolder':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        render: (_text: string, record: IDataIngestionTableRow) => {
                            if (record?.isEditing && field) {
                                return renderTextInputWithShowMore(
                                    record,
                                    field,
                                    showSearchModal,
                                    'SelectIP',
                                    CONTRIBUTORS_KEY,
                                    fileMetadataChanged
                                );
                            }

                            return record[field?.name] || '';
                        }
                    };
                case 'shoppingCart':
                    return {
                        title: field.data,
                        dataIndex: field.name,
                        key: field.name,
                        render: (record) => renderTextInputWithShowMore(record, field, showSearchModal, 'Select Shopping Cart', SEARCH_VIEW_LICENSES_FILEIMPORT, fileMetadataChanged)
                    };
                default:
                    return null;
            }
        }).filter((col): col is Exclude<typeof col, null> => col !== null);
        return [getActionIconsColumn(), ...fieldsToRender, getDynamicActionColumn()];
    }, [
        props.storagePathItemData,
        props.selectedFileSystem?.fullName,
        props.distributions,
        props.pools,
        props.formats,
        props.sources,
        props.destinations,
        props.containerDetailsWindowComponentData,
        props.fileMetadataChanged,
        props.displayAttachmentType,
        props.displayRemoveColumn,
        props.attchmentTypeLookupOptions,
        props.showSearchModal,
        props.canEditMetadata,
        props.determineIsReadOnly,
        filteredSources,
        sourceById,
        sourceByHierarchy,
        poolsBySourceId,
        poolByIdOrCode,
        distributionsByPoolCode,
        attachmentTypeCodes
    ]);

    const data = useMemo(() => {
        const storagePaths = props.storagePaths;
        if (storagePaths.length !== 0) {
            for (var storageItem of storagePaths) {
                if (storageItem.metadata != null) {
                    for (var job of props.jobs) {
                        if (storageItem.metadata?.jobID == job.jobID) {
                            storageItem.inProgress = true;
                        }
                    }
                }
            }
        }        

        return storagePaths.map(row => ({
            key: row?.fullName,
            FilenameTableHeader: row?.name,
            distributionCode: row?.metadata?.distributionCode,
            poolCode: row?.metadata?.poolCode,
            format: row?.metadata?.format,
            outputFormat: row?.metadata?.outputFormat,
            source: row?.metadata?.source,
            destination: row?.metadata?.destination,
            fileSize: row?.metadata?.fileSize,
            LastModifiedTableHeader: row?.lastModified,
            TrackingInformationTableHeader: row?.metadata?.trackingInformation,
            inProgress: row?.inProgress,
            isEditing: row?.isEditing,
            isDirectory: row?.isDirectory,
            isDirty: row?.isDirty,
            attachmentTypeCode: row?.attachmentTypeCode,
            attachmentTypeID: row?.attachmentTypeID,
            rightHolder: row?.metadata?.rightHolder,
            batchID: row?.metadata?.batchID,
            validationError: row?.validationError,
        }));


    }, [props.storagePaths, props.jobs]);

    const onRowClick = (record: IDataIngestionTableRow) => {
        const { openDirectory, sources } = props;
        var fullName = record?.key;
        var name = record?.FilenameTableHeader
        if (record?.isDirectory) {
            openDirectory({
                fullName,
                name,
                fileSystems: undefined,
                canEditMetadata: undefined,
                hasMetadata: undefined,
                jobTypes: undefined,
                parent: undefined,
                path: fullName
            },
                sources);
        }
    }

    const expandedRowKeys = useMemo(() => data.filter(row => row.validationError).map(row => row.key), [data]);
	
    return (
        <div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={{
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100, 250, 500, 1000],
                    position: ["bottomRight", "topRight"],
                    showTotal: (total) => (
                        <b>{total} {props.viewData['results']}</b>
                    ),
                    locale: { items_per_page: props.viewData['itemsPerPage'] }
                }}
                onRow={(record) => ({
                    onClick: () => onRowClick(record)
                })}
                size={'middle'}
                className={"parent-table"}
                expandable={{
                    expandedRowRender: (record) =>
                        record.validationError ? (
                            <div className="text-danger">{record.validationError}</div>
                        ) : null,
                    expandedRowKeys: expandedRowKeys,
                    expandIconColumnIndex: -1,
                    expandIcon: () => null
                }}
                locale={{
                    emptyText: props.isRepertoireModule ? (
                        <div className="dragDropEmptyState">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={props.viewData[NO_DATA]}
                            />
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={props.viewData[NO_DATA]}
                        />
                    ),
                    triggerDesc: props.viewData[SORT_DESCENDING],
                    triggerAsc: props.viewData[SORT_ASCENDING],
                    cancelSort: props.viewData[CANCEL_SORTING],
                }}
            />
        </div>
    );
};

export default DataIngestionTable;
